import { Session, SessionRole } from '../types';

/**
 * JWT payload structure from the CMS token
 */
interface JWTPayload {
  sessionId: string;
  storeId: string;
  accountId: string;
  customerId: string;
  role: SessionRole;
  iat?: number;
  exp?: number;
}

/**
 * Decode a JWT token to extract session information
 * This is a simple client-side decoder - do not use for security validation
 */
export function decodeJWTClient(token: string): Session | null {
  try {
    // Split the token into parts
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('Invalid JWT token format');
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];

    // Add padding if needed
    const paddedPayload = payload + '='.repeat((4 - (payload.length % 4)) % 4);

    // Decode base64
    const decoded = atob(paddedPayload);

    // Parse JSON
    const parsedPayload: JWTPayload = JSON.parse(decoded);

    // Check if token is expired
    if (parsedPayload.exp && Date.now() >= parsedPayload.exp * 1000) {
      console.warn('JWT token is expired');
      return null;
    }

    // Return session data
    return {
      sessionId: parsedPayload.sessionId,
      storeId: parsedPayload.storeId,
      accountId: parsedPayload.accountId,
      customerId: parsedPayload.customerId,
      role: parsedPayload.role,
      isValid: true,
      isLoading: false,
    };
  } catch (error) {
    console.error('Failed to decode JWT token:', error);
    return null;
  }
}

/**
 * Check if a JWT token is expired
 */
export function isTokenExpired(token: string): boolean {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;

    const payload = parts[1];
    const paddedPayload = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    const decoded = atob(paddedPayload);
    const parsedPayload: JWTPayload = JSON.parse(decoded);

    if (parsedPayload.exp) {
      return Date.now() >= parsedPayload.exp * 1000;
    }

    return false;
  } catch (error) {
    console.error('Failed to check token expiration:', error);
    return true;
  }
}

/**
 * Get token expiration time
 */
export function getTokenExpiration(token: string): Date | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    const paddedPayload = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    const decoded = atob(paddedPayload);
    const parsedPayload: JWTPayload = JSON.parse(decoded);

    if (parsedPayload.exp) {
      return new Date(parsedPayload.exp * 1000);
    }

    return null;
  } catch (error) {
    console.error('Failed to get token expiration:', error);
    return null;
  }
}

